import { DTOOLS_BANNED_OPTION_COPY_PHRASES } from "./dtoolsBannedOptionCopy";
import { collectOptionPrimaryComponentBlocks } from "./dtoolsOptionComponentGroups";
import { inferDtoolsEquipmentBenefit, inferDtoolsEquipmentRole } from "./dtoolsEquipmentRoles";
import type { DtoolsReviewSystem } from "./dtoolsStagingTransform";

const WEAK_HIGHLIGHT_PATTERNS = [
  /^supporting components?$/i,
  /^services?$/i,
  /^integration$/i,
  /^quoted equipment$/i,
  /^equipment$/i,
  /^\d+\s*-?\s*zone\b/i,
  /^\d+\.\d+\.\d+$/i,
  /\b\d+\s*watt\b/i,
  /\b\d+\s*channels?\b/i,
];

const GENERIC_FILLER_PATTERNS = [
  /complete performance system/i,
  /selected to work together as one coordinated system/i,
  /selected to work together as one cohesive experience/i,
  /covers the quoted equipment for this scope/i,
  /engineered for immersive results/i,
  /brings together the amplification and projection components/i,
  /is built around amplification, projection/i,
  /option\s+\d+\s+brings together/i,
  ...DTOOLS_BANNED_OPTION_COPY_PHRASES.map((phrase) => new RegExp(phrase.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "i")),
];

export function isWeakCustomerHighlight(text: string): boolean {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return true;
  return WEAK_HIGHLIGHT_PATTERNS.some((pattern) => pattern.test(trimmed));
}

export function isGenericOptionOverview(text: string): boolean {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return true;
  return GENERIC_FILLER_PATTERNS.some((pattern) => pattern.test(trimmed));
}

function lineHaystack(blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>): string {
  return blocks
    .map((block) =>
      [
        block.line.manufacturer,
        block.line.model,
        block.line.category,
        block.line.polishedDescription,
        block.line.dtoolsDescription,
        block.line.cleoDescription,
      ]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean)
        .join(" "),
    )
    .join(" ");
}

function quotedProductLabels(blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>, limit = 3): string[] {
  const labels: string[] = [];
  for (const block of blocks) {
    const label = [block.line.manufacturer, block.line.model].filter(Boolean).join(" ").trim();
    if (!label) continue;
    if (!labels.some((existing) => existing.toLowerCase() === label.toLowerCase())) {
      labels.push(label);
    }
    if (labels.length >= limit) break;
  }
  return labels;
}

function labelForBlock(block: ReturnType<typeof collectOptionPrimaryComponentBlocks>[number]): string {
  return [block.line.manufacturer, block.line.model].filter(Boolean).join(" ").trim();
}

function blockHaystack(block: ReturnType<typeof collectOptionPrimaryComponentBlocks>[number]): string {
  return [
    block.line.category,
    block.line.manufacturer,
    block.line.model,
    block.line.polishedDescription,
    block.line.dtoolsDescription,
  ]
    .map((v) => String(v ?? "").trim())
    .filter(Boolean)
    .join(" ");
}

function isAmplificationBlock(block: ReturnType<typeof collectOptionPrimaryComponentBlocks>[number]): boolean {
  const hay = blockHaystack(block);
  return /\b(amplifier|power amp|multi-?channel amp|arcam pa\d+)\b/i.test(hay);
}

function findProductLabel(
  blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>,
  matcher: RegExp,
  options?: { excludeRoles?: string[] },
): string | null {
  for (const block of blocks) {
    if (isAmplificationBlock(block)) continue;
    const role = inferDtoolsEquipmentRole(block.line);
    if (options?.excludeRoles?.includes(role)) continue;
    const label = labelForBlock(block);
    const hay = blockHaystack(block);
    if (matcher.test(hay) && label) return label;
  }
  return null;
}

function findProjectorLabel(blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>): string | null {
  for (const block of blocks) {
    if (isAmplificationBlock(block)) continue;
    const hay = blockHaystack(block);
    if (/\bdream\s*vision|\beos\s*light\b|\bprojector\b/i.test(hay)) {
      const label = labelForBlock(block);
      if (label) return label;
    }
  }
  for (const block of blocks) {
    if (isAmplificationBlock(block)) continue;
    const role = inferDtoolsEquipmentRole(block.line);
    if (role === "Projection" || role === "Display") {
      const label = labelForBlock(block);
      if (label) return label;
    }
  }
  return null;
}

function overviewSubject(displayName: string, fallback = "This cinema proposal"): string {
  const trimmed = displayName.trim();
  const withSuffix = trimmed.match(/^option\s+\d+\s*[—–-]\s*(.+)$/i);
  if (withSuffix?.[1]?.trim()) return `The ${withSuffix[1].trim()} package`;
  if (/^option\s+\d+$/i.test(trimmed)) return fallback;
  return `The ${trimmed} proposal`;
}

function isPrivateCinemaSystem(
  blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>,
  haystack: string,
): boolean {
  const roles = blocks.map((block) => inferDtoolsEquipmentRole(block.line));
  const hasProjection = roles.some((role) =>
    ["Projection", "Video Processing", "Display"].includes(role),
  );
  const hasAmplification = roles.some((role) => role === "Amplification");
  const cinemaKeywords =
    /\b(cinema|theatre|theater|projector|madvr|kaleidescape|arcam|dream\s*vision|jbl\s*synthesis)\b/i.test(
      haystack,
    );
  return cinemaKeywords || (hasProjection && hasAmplification);
}

function buildPrivateCinemaOverview(
  blocks: ReturnType<typeof collectOptionPrimaryComponentBlocks>,
  displayName: string,
): string {
  const subject = overviewSubject(displayName);
  const projector = findProjectorLabel(blocks);
  const videoProcessor = findProductLabel(blocks, /\bmadvr|\benvy\b/i, {
    excludeRoles: ["Amplification", "Projection", "Display"],
  });
  const surroundProcessor =
    findProductLabel(blocks, /\bjbl\s*synthesis|\bsdp-?58\b|\btrinnov\b|\baltitude\b/i, {
      excludeRoles: ["Video Processing", "Amplification", "Projection", "Display"],
    }) ??
    findProductLabel(blocks, /\bsurround processor|immersive processor|audio processing\b/i, {
      excludeRoles: ["Video Processing", "Amplification", "Projection", "Display"],
    });
  const ampLabels = blocks
    .filter((block) => inferDtoolsEquipmentRole(block.line) === "Amplification")
    .map((block) => labelForBlock(block))
    .filter(Boolean)
    .slice(0, 2);
  const kaleidescape = findProductLabel(blocks, /kaleidescape/i);

  const sentences: string[] = [
    `${subject} is designed to give the room a serious, full-scale movie experience without overcomplicating the system.`,
  ];

  const imagePart =
    projector && videoProcessor
      ? `The ${projector} and ${videoProcessor} work together to create a sharper, more cinematic image`
      : projector
        ? `The ${projector} anchors a large-format cinema image in the room`
        : videoProcessor
          ? `${videoProcessor} improves motion and colour for a more cinematic picture`
          : null;

  const ampPhrase = ampLabels.length > 0 ? ampLabels.join(" / ") : null;
  const soundPart =
    surroundProcessor && ampPhrase
      ? `the ${surroundProcessor} processor and ${ampPhrase} amplification provide the control and power needed for immersive surround sound`
      : surroundProcessor
        ? `the ${surroundProcessor} processor manages immersive surround sound with the amplification quoted alongside it`
        : ampPhrase
          ? `${ampPhrase} provide dedicated power for immersive surround playback`
          : null;

  if (imagePart && soundPart) {
    sentences.push(`${imagePart}, while ${soundPart}.`);
  } else if (imagePart) {
    sentences.push(`${imagePart}.`);
  } else if (soundPart) {
    sentences.push(`${soundPart.charAt(0).toUpperCase()}${soundPart.slice(1)}.`);
  }

  if (kaleidescape) {
    sentences.push(
      `${kaleidescape} gives the system a high-quality movie source, so the picture and sound are not limited by streaming compression.`,
    );
  }

  sentences.push(
    "In short, this option focuses on the core ingredients that make a private cinema feel convincing: image scale, surround immersion, clean power, and reliable integration.",
  );

  return sentences.join(" ");
}

/** Customer-facing executive overview when no edited copy exists. */
export function buildCustomerFacingOptionOverview(system: DtoolsReviewSystem, displayName: string): string {
  const blocks = collectOptionPrimaryComponentBlocks(system);
  const haystack = lineHaystack(blocks);

  if (/\b(lutron|homeworks|radio ra|ketra|lighting control|lighting system)\b/i.test(haystack)) {
    return `${displayName} provides the core Lutron control equipment required for the home’s lighting controls to work together. It allows keypads, scenes, schedules and connected controls to operate as one coordinated system — so everyday moments such as Welcome, Entertain, Movie Night, or Good Night feel simple and reliable.`;
  }

  if (isPrivateCinemaSystem(blocks, haystack)) {
    return buildPrivateCinemaOverview(blocks, displayName);
  }

  if (/\b(projector|dream\s*vision|sony|jvc|laser)\b/i.test(haystack)) {
    const products = quotedProductLabels(blocks, 2);
    const productPhrase = products.length ? products.join(" and ") : "the projection system";
    const subject = overviewSubject(displayName, "This proposal");
    return `${subject} centres on ${productPhrase} to deliver a large-format cinema image in the dedicated viewing space, with processing and amplification chosen to support realistic playback levels.`;
  }

  const roles = blocks.map((block) => inferDtoolsEquipmentRole(block.line));
  if (roles.some((role) => role === "Amplification") && /\b(arcam|amplifier|power amp)\b/i.test(haystack)) {
    const products = quotedProductLabels(blocks, 2);
    const productPhrase = products.length ? products.join(" and ") : "the amplification quoted";
    const subject = overviewSubject(displayName, "This proposal");
    return `${subject} includes ${productPhrase} to drive the speaker system with stable, high-current power so music and cinema playback stay controlled at realistic listening levels.`;
  }

  if (roles.some((role) => role === "Automation") && !/\b(lutron|homeworks)\b/i.test(haystack)) {
    const subject = overviewSubject(displayName, "This proposal");
    return `${subject} includes the control and automation equipment needed to bring the quoted systems under one reliable interface for everyday use in the home.`;
  }

  const benefits = blocks
    .map((block) => inferDtoolsEquipmentBenefit(block.line))
    .filter((benefit) => benefit && !isWeakCustomerHighlight(benefit));

  const unique: string[] = [];
  for (const benefit of benefits) {
    if (!unique.some((existing) => existing.toLowerCase() === benefit.toLowerCase())) {
      unique.push(benefit);
    }
    if (unique.length >= 3) break;
  }

  const products = quotedProductLabels(blocks, 3);
  const subject = overviewSubject(displayName, "This proposal");

  if (products.length >= 2) {
    const subject = overviewSubject(displayName, "This proposal");
    const focus = unique.length >= 2 ? unique[0]!.toLowerCase() : "the core capabilities quoted";
    return `${subject} is designed around ${focus}, with supporting equipment completing the experience rather than reading as a parts list.`;
  }

  if (unique.length === 1) {
    return `${subject} centres on ${unique[0]!.toLowerCase()}, with supporting equipment quoted to complete this scope.`;
  }

  if (unique.length >= 2) {
    const focus = unique
      .slice(0, -1)
      .map((b) => b.toLowerCase())
      .join(", ");
    const last = unique[unique.length - 1]!.toLowerCase();
    return `${subject} combines ${focus}, and ${last} for the experience this package is designed to deliver.`;
  }

  if (products.length === 1) {
    return `${subject} includes ${products[0]} and the supporting equipment quoted for this scope.`;
  }

  return `${subject} covers the equipment quoted for this home-technology scope.`;
}
